// src/components/features/chat/MarkerTrendWidget.jsx
//
// Inline marker trend widget rendered inside chat when Claude calls
// the show_marker_trend tool.
//
// ⚠ Constitution §III — AI Prohibition Zones:
//   This widget displays RAW NUMERICAL DATA only.
//   Claude's accompanying text must NOT interpret the trend clinically
//   (e.g. "your LDL is dangerously high" is prohibited).
//   The system prompt governs this constraint.
//   PENDING Dr. Ajay Kumar sign-off on show_marker_trend tool addition
//   before this goes live. See CONTEXT_frontend.md — Pre-go-live sign-offs.
//
// Props:
//   data: Array<{ drawDate: string, value: number, unit: string, status: string }>
//   markerName: string
//   unit: string

import React from 'react';

const STATUS_COLOR = {
  normal:   'var(--green)',
  elevated: 'var(--amber)',
  low:      'var(--amber)',
  critical: 'var(--red)',
  high:     'var(--red)',
};

function statusColor(s) {
  if (!s) return 'var(--text-muted)';
  return STATUS_COLOR[s.toLowerCase()] || 'var(--text-muted)';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: '2-digit',
    });
  } catch { return iso; }
}

// ── SVG Sparkline ─────────────────────────────────────────────────────────────
function Sparkline({ points, color, width = 240, height = 52 }) {
  if (!points || points.length < 2) return null;
  const values = points.map(p => p.value);
  const min    = Math.min(...values);
  const max    = Math.max(...values);
  const range  = max - min || 1;
  const pad    = 6;

  const coords = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * (width - pad * 2);
    const y = pad + (1 - (p.value - min) / range) * (height - pad * 2);
    return { x, y, ...p };
  });

  const pathD = `M ${coords.map(c => `${c.x},${c.y}`).join(' L ')}`;
  const last  = coords[coords.length - 1];
  const first = coords[0];

  return (
    <svg
      width={width} height={height}
      style={{ overflow: 'visible', display: 'block' }}
      aria-label={`Trend chart`}
    >
      {/* Gradient fill under line */}
      <defs>
        <linearGradient id="mg-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`${pathD} L ${last.x},${height} L ${first.x},${height} Z`}
        fill="url(#mg-fill)"
      />
      {/* Main line */}
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Data points */}
      {coords.map((c, i) => (
        <circle
          key={i}
          cx={c.x} cy={c.y} r={i === coords.length - 1 ? 4 : 3}
          fill={i === coords.length - 1 ? color : 'var(--bg-surface)'}
          stroke={color}
          strokeWidth="1.5"
        />
      ))}
    </svg>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────
export default function MarkerTrendWidget({ data, markerName, unit }) {
  if (!data || data.length === 0) {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'var(--bg-subtle)',
        borderRadius: 'var(--radius-md)',
        fontSize: 13, color: 'var(--text-muted)',
        fontFamily: 'var(--font-body)',
      }}>
        No historical data found for {markerName}.
      </div>
    );
  }

  const sorted   = [...data].sort((a, b) => new Date(a.drawDate) - new Date(b.drawDate));
  const latest   = sorted[sorted.length - 1];
  const previous = sorted.length > 1 ? sorted[sorted.length - 2] : null;
  const delta    = previous ? (latest.value - previous.value) : null;
  const trendColor = statusColor(latest.status);

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '16px',
      fontFamily: 'var(--font-body)',
      maxWidth: 340,
      marginTop: 6,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 12,
      }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 2,
          }}>
            {markerName}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontSize: 26, fontWeight: 600, color: trendColor, lineHeight: 1,
            }}>
              {latest.value}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {unit || latest.unit}
            </span>
          </div>
          {latest.status && (
            <div style={{
              fontSize: 11, fontWeight: 600, color: trendColor,
              textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 2,
            }}>
              {latest.status}
            </div>
          )}
        </div>

        {/* Delta vs previous draw */}
        {delta !== null && (
          <div style={{
            fontSize: 13, fontWeight: 600,
            color: delta > 0 ? 'var(--red)' : delta < 0 ? 'var(--green)' : 'var(--text-muted)',
            textAlign: 'right',
          }}>
            {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'}{' '}
            {Math.abs(delta).toFixed(1)} {unit || latest.unit}
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>
              vs prev draw
            </div>
          </div>
        )}
      </div>

      {/* Sparkline */}
      {sorted.length >= 2 && (
        <div style={{ marginBottom: 12 }}>
          <Sparkline
            points={sorted}
            color={trendColor}
            width={300}
            height={52}
          />
          {/* X-axis labels */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: 4,
          }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {formatDate(sorted[0].drawDate)}
            </span>
            {sorted.length > 2 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {sorted.length} draws
              </span>
            )}
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {formatDate(sorted[sorted.length - 1].drawDate)}
            </span>
          </div>
        </div>
      )}

      {/* Draw-by-draw table */}
      <div style={{
        borderTop: '1px solid var(--border)', paddingTop: 10,
      }}>
        {[...sorted].reverse().slice(0, 5).map((row, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', paddingBottom: 5, marginBottom: 5,
            borderBottom: i < Math.min(sorted.length, 5) - 1
              ? '1px solid var(--bg-subtle)' : 'none',
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {formatDate(row.drawDate)}
              {i === 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontWeight: 600,
                  color: 'var(--brand-accent)',
                }}>Latest</span>
              )}
            </span>
            <span style={{
              fontSize: 13, fontWeight: i === 0 ? 600 : 400,
              color: statusColor(row.status),
            }}>
              {row.value} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{unit || row.unit}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Disclaimer — Constitution §III */}
      <div style={{
        marginTop: 8, fontSize: 10, color: 'var(--text-muted)',
        lineHeight: 1.4,
      }}>
        Raw lab values. Always verify with your care team.
      </div>
    </div>
  );
}
